import React from 'react';

import type { FormSelectProps } from './forms.types';
import type { SelectState } from '@/types/forms';

const FormSelect = React.forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    {
      label,
      error,
      success,
      helpText,
      required = false,
      size = 'md',
      options,
      placeholder,
      className = '',
      disabled = false,
      id,
      ...props
    },
    ref
  ) => {
    const reactId = React.useId();
    const selectId = id || `select-${reactId}`;

    // Determine state based on props
    const state: SelectState = error
      ? 'error'
      : success
        ? 'success'
        : 'default';

    const sizeClasses = {
      sm: 'px-3 py-1.5 text-body-lg md:text-body-sm pr-8',
      md: 'px-4 py-3 text-body-lg md:text-body-sm pr-10',
      lg: 'px-4 py-3.5 text-body-lg md:text-body-md pr-12',
    };

    // Dark Sakura theme state classes
    const stateClasses = {
      default: 'border-stroke-default focus:border-primary-400/50',
      error: 'border-error-500 focus:border-error-500',
      success: 'border-success-500 focus:border-success-500',
    };

    const labelStateClasses = {
      default: 'text-neutral-300',
      error: 'text-error-400',
      success: 'text-success-400',
    };

    const baseClasses =
      'appearance-none block w-full border rounded-lg bg-contrast-700 text-neutral-50 focus:outline-none focus-visible:ring-state-focus-inset transition-colors cursor-pointer';

    const disabledClasses = disabled
      ? 'bg-contrast-700 opacity-60 cursor-not-allowed'
      : '';

    const classes = [
      baseClasses,
      sizeClasses[size],
      stateClasses[state],
      disabledClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Determine message to display
    const message = error || success || helpText;
    const messageId = `${selectId}-message`;

    // Chevron icon size based on select size
    const chevronSize = {
      sm: 'h-4 w-4',
      md: 'h-5 w-5',
      lg: 'h-6 w-6',
    };

    const chevronPosition = {
      sm: 'right-2',
      md: 'right-3',
      lg: 'right-4',
    };

    return (
      <div className="space-y-1.5">
        {label && (
          <label
            htmlFor={selectId}
            className={`block text-body-sm font-medium ${labelStateClasses[state]}`}
          >
            {label}
            {required && <span className="text-primary-400 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          <select
            ref={ref}
            id={selectId}
            className={classes}
            disabled={disabled}
            aria-invalid={state === 'error'}
            aria-describedby={message ? messageId : undefined}
            {...props}
          >
            {placeholder && (
              <option value="" disabled className="text-neutral-500">
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className="bg-contrast-700 text-neutral-50"
              >
                {option.label}
              </option>
            ))}
          </select>

          {/* Custom chevron icon */}
          <div
            className={`pointer-events-none absolute inset-y-0 ${chevronPosition[size]} flex items-center text-neutral-500`}
          >
            <svg
              className={chevronSize[size]}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {message && (
          <p
            id={messageId}
            className={`text-label-xs ${
              error
                ? 'text-error-400'
                : success
                  ? 'text-success-400'
                  : 'text-neutral-400'
            }`}
          >
            {message}
          </p>
        )}
      </div>
    );
  }
);

FormSelect.displayName = 'FormSelect';

export default FormSelect;
